/**
 * Wraps a promise with console timing logs so slow or failing loader
 * requests are immediately visible in the browser console.
 *
 * Usage:
 *   timed("carts.getAll", Crud.carts.getAll(...))
 */
export async function timed<T>(label: string, promise: Promise<T>): Promise<T> {
  const start = performance.now();
  try {
    return await promise;
  } catch (err: any) {
    const ms = Math.round(performance.now() - start);

    const info: Record<string, unknown> = {
      label,
      durationMs: ms,
      timestamp: new Date().toISOString(),
      page: window.location.href,
    };

    // Axios-specific details
    if (err?.isAxiosError) {
      info.errorCode = err.code; // e.g. "ECONNABORTED" for timeouts
      info.requestUrl = err.config?.url;
      info.requestMethod = err.config?.method?.toUpperCase();
      info.requestBaseURL = err.config?.baseURL;
      info.httpStatus = err.response?.status ?? null;
      info.responseData = err.response?.data ?? null;
      info.isTimeout =
        err.code === "ECONNABORTED" || err.message?.includes("timeout");
    } else {
      info.errorMessage = err?.message;
      info.errorName = err?.name;
    }

    console.error(
      `[TIMEOUT ERROR] ✗ ${label} — failed after ${ms}ms`,
      JSON.stringify(info, null, 2),
      JSON.stringify(err, null, 2)
    );
    throw err;
  }
}
